import { useState } from "react";
import { Navigate, createFileRoute } from "@tanstack/react-router";
import { Table } from "react-bootstrap";
import { useTranslation, Trans } from "react-i18next";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import Card from "../../../../components/core/basic/Card";
import checkTierAccess from "../../../../utils/checkTierAccess";
import { trellisPalette } from "../../../../components/custom/analytics/palettes";
import { Button } from "../../../../components/core/basic/Button";
import { ImportButton } from "../../../../components/custom/import/ImportButton";
import { Accordion } from "../../../../components/core/basic/Accordion";
import { DevErrorDisplay } from "../../../../components/core/blocks/DevErrorDisplay";
import { toTitleCase } from "../../../../utils/formatText";
import { useAppSelector } from "../../../../redux/hooks";
import { usePricingConfiguration } from "../../../../modules/pricingv2/api/usePricingConfiguration";
import Loading from "../../../../components/core/blocks/Loading";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import { css } from "../../../../../styled-system/css";
import { Center, Container, Grid, HStack, VStack } from "../../../../../styled-system/jsx";
import { Select } from "../../../../components/fields/formily/Select";
import { ExplainerBox } from "../../../../components/core/basic/ExplainerBox";
import { NumberInput, OffsetInput } from "../../../../components/fields/formily/NumberInput";
import { NumberInput as NumberInputBase } from "../../../../components/fields/base/NumberInput";
import { type Offset } from "../../../../zodios/apiDefinitions/pricing";
import { useFormily } from "../../../../hooks/useFormily";
import { ArrayField, Field, FormConsumer, FormProvider, observer, useForm } from "@formily/react";
import { TextField } from "../../../../components/fields/base/TextField";
import { applyOffset } from "../../../../modules/pricingv2/utils";
import { isField } from "@formily/core";
import { Checkbox } from "../../../../components/fields/formily/Checkbox";
import { Checkbox as BaseCheckbox } from "../../../../components/fields/base/Checkbox";
import { successToast, errorToast } from "../../../../utils/toasts";
import type { PricingOrg } from "../../../../zodios/apiDefinitions/pricing";
import { useUpdatePricingConfiguration } from "../../../../modules/pricingv2/api/useUpdatePricingConfiguration";
import { gridItem } from "../../../../../styled-system/patterns";
import { useSimpleCan } from "../../../../config/casl/casl";
import UnauthorizedWarning from "../../../../components/core/basic/UnauthorizedWarning";
import NiceModal from "@ebay/nice-modal-react";
import { PricingPlanBulkUploadModalV2 } from "../../../../modules/pricingv2/PricingPlanBulkUploadV2";
import { PriceLinkUploadModal } from "../../../../modules/pricingv2/PriceLinkUpload";

const defaultInventoryRules = [
  {
    status: "deficient",
    min: 0,
    max: 50,
  },
  {
    status: "healthy",
    min: 50,
    max: 150,
  },
  {
    status: "excess",
    min: 150,
    max: 300,
  },
  {
    status: "overstock",
    min: 300,
    max: 99999999,
  },
];

const RULE_TO_STRATEGY_KEY = {
  deficient: "Increase Prices",
  healthy: "Balanced Strategy",
  excess: "Decrease Prices",
  overstock: "Aggressive Price Reduction",
  default: "Unknown",
} as const;

function getInventoryRuleStatus(
  current: number,
  desired: number,
  rules: { min: number; max: number; status: string }[],
) {
  for (const rule of rules) {
    const minLevel = (rule.min / 100) * desired;
    const maxLevel = (rule.max / 100) * desired;

    if (current >= minLevel && current <= maxLevel) {
      return rule.status as "deficient" | "healthy" | "excess" | "overstock";
    }
  }

  return null;
}

function FbaInventoryFieldsControl({
  value,
  onChange,
  isDisabled,
}: {
  value: string[] | null;
  onChange: (fields: string[] | null) => void;
  isDisabled: boolean;
}) {
  const { t } = useTranslation(["pricingv2"]);
  const selected = new Set(value ?? ["afn-fulfillable-quantity"]);

  const fbaFieldOptions = [
    { key: "afn-fulfillable-quantity", label: t(($) => $.pricingv2["Fulfillable Quantity"]), alwaysOn: true },
    { key: "afn-reserved-quantity", label: t(($) => $.pricingv2["Reserved Quantity"]), alwaysOn: false },
  ] as const;

  const handleToggle = (key: string, checked: boolean) => {
    const next = new Set(selected);
    if (checked) {
      next.add(key);
    } else {
      next.delete(key);
    }
    const hasExtras = [...next].some((k) => k !== "afn-fulfillable-quantity");
    onChange(hasExtras ? [...next] : null);
  };

  return (
    <VStack gap="xsmall" alignItems="start">
      {fbaFieldOptions.map((opt) => (
        <BaseCheckbox
          key={opt.key}
          label={opt.label}
          value={selected.has(opt.key)}
          isDisabled={isDisabled || opt.alwaysOn}
          onChange={(checked) => handleToggle(opt.key, checked)}
        />
      ))}
    </VStack>
  );
}

const Settings = () => {
  const { t } = useTranslation(["pricingv2"]);
  const user = useAppSelector((state) => state.user);
  const amz_onboard = useAppSelector((state) => state.amz_onboard);
  const hasPriceOptimizationModule = useModuleEnabled("PRICE_OPTIMIZATION");
  const hasDebugModuleEnabled = useModuleEnabled("DEBUG");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importRelationshipsModalOpen, setImportRelationshipsModalOpen] = useState(false);
  const configurationQuery = usePricingConfiguration();
  const updateMutation = useUpdatePricingConfiguration();
  const userCanUpdatePricingPlans = useSimpleCan("update", "PricingPlan");

  if (
    ((!user.pricing_tier || user.pricing_tier === "unknown") &&
      !hasPriceOptimizationModule &&
      !checkTierAccess(user.tier, "pricing", amz_onboard.org_status)) ||
    user.pricing_tier === "MANUALLY_DISABLED"
  ) {
    return <Navigate to="/user/pricing/dashboard" replace />;
  }

  if (configurationQuery.isLoading) {
    return <Loading fullPage text={t(($) => $.pricingv2["Loading Configuration"])} />;
  }

  if (!configurationQuery.isSuccess) {
    return (
      <DevErrorDisplay
        userMessage={t(($) => $.pricingv2["Failed to load configuration"])}
        error={configurationQuery.error}
      />
    );
  }

  return (
    <PageContainer>
      {userCanUpdatePricingPlans ?
        <Container>
          <h1
            className={css({
              margin: "0",
              padding: "small",
              color: "text.heading",
              fontSize: "xxlarge",
              fontWeight: "bold",
              textAlign: "center",
            })}
          >
            {t(($) => $.pricingv2["Dynamic Pricing - Configuration"])}
          </h1>
          <HStack gap={"large"} justify={"center"} padding="medium">
            <ImportButton
              defaultType={`pricing_bulk_upload`}
              importModalOpen={importModalOpen}
              setImportModalOpen={setImportModalOpen}
              title={t(($) => $.pricingv2["Upload Pricing Data"])}
            />
            <ImportButton
              defaultType={`pricing_relationships`}
              importModalOpen={importRelationshipsModalOpen}
              setImportModalOpen={setImportRelationshipsModalOpen}
              title={t(($) => $.pricingv2["Upload Relationships"])}
            />
            {user.is_staff && (
              <Button onPress={() => void NiceModal.show(PriceLinkUploadModal)}>
                {t(($) => $.pricingv2["Upload Price Links"])}
              </Button>
            )}
            {hasDebugModuleEnabled && (
              <Button onPress={() => void NiceModal.show(PricingPlanBulkUploadModalV2)}>
                {t(($) => $.pricingv2["Bulk Upload Pricing Plans"])}
              </Button>
            )}
          </HStack>
          <PricingConfigurationForm
            data={configurationQuery.data}
            onSubmit={async (values) => {
              await updateMutation.mutateAsync(
                {
                  ...values,
                  inventory_bias: values.inventory_bias || null,
                  porgId: configurationQuery.data.id,
                },
                {
                  onSuccess: () => {
                    successToast({
                      message: t(($) => $.pricingv2["Pricing settings have been saved"]),
                    });
                  },
                  onError: (error) => {
                    errorToast({ message: t(($) => $.pricingv2["Error saving pricing settings"]), error });
                  },
                },
              );
            }}
          />
        </Container>
      : <UnauthorizedWarning />}
    </PageContainer>
  );
};

function ConfPageLabel({ label, description }: { label: string; description: string }) {
  return (
    <VStack gap="xsmall" alignItems={"start"}>
      <h5 className={css({ margin: 0, color: "text.heading", fontSize: "xlarge", fontWeight: 700 })}>{label}</h5>
      <div className={css({ color: "text.heading", fontSize: "small" })}>{description}</div>
    </VStack>
  );
}

type FormValues = Omit<PricingOrg, "inventory_bias"> & {
  has_inventory_rules: boolean;
  inventory_rules: Exclude<PricingOrg["supply_target_program"], null>["rules"] | null;
  b2b_offset: Offset;
  list_price_offset: Offset;
  inventory_bias: "FBA" | "MFN" | "";
};

function porgToFormValues(porg: PricingOrg): FormValues {
  const result = {
    ...porg,
    inventory_bias: porg.inventory_bias ?? ("" as const),
    has_inventory_rules: !!porg.supply_target_program,
    inventory_rules: porg.supply_target_program ? porg.supply_target_program.rules : null,
    b2b_offset: {
      amount: porg.b2b_offset_amount,
      type: porg.b2b_offset_type,
    },
    list_price_offset: {
      amount: porg.list_price_offset_amount,
      type: porg.list_price_offset_type,
    },
  };

  return result;
}

const PricesErrorMessage = observer(() => {
  const field = useForm().getFieldState("send_standard_price");
  return field.selfErrors ?
      <p className={gridItem({ colSpan: 2, margin: 0, color: "error", fontSize: "small" })}>{field.selfErrors[0]}</p>
    : null;
});

function PricingConfigurationForm({
  data,
  onSubmit,
}: {
  data: Exclude<ReturnType<typeof usePricingConfiguration>["data"], undefined>;
  onSubmit: (values: FormValues) => void | Promise<void>;
}) {
  const { t } = useTranslation(["pricingv2"]);
  const userCanUpdateOrgSettings = useSimpleCan("update", "Org");

  const form = useFormily<FormValues>({
    initialValues: porgToFormValues(data),
  });

  const [testInventoryLevel, setTestInventoryLevel] = useState(40);
  const [testDesiredInventoryLevel, setTestDesiredInventoryLevel] = useState(30);
  return (
    <FormProvider form={form}>
      <FormConsumer>
        {() => (
          <Card css={{ padding: "large" }}>
            <Grid
              columnGap={"xlarge"}
              rowGap="medium"
              alignItems={"start"}
              gridTemplateColumns={"minmax(0,2fr) minmax(0,1fr)"}
              gridAutoRows={"1fr"}
            >
              <ConfPageLabel
                label={t(($) => $.pricingv2["Charm Pricing Style"])}
                description={t(($) => $.pricingv2["The digits you want to end your price with. i.e. 7.99, 129.95"])}
              />

              <Field
                name={"end_digits"}
                component={[Select, { isDisabled: !userCanUpdateOrgSettings }]}
                dataSource={[
                  { value: "0.99", label: "0.99" },
                  { value: "0.98", label: "0.98" },
                  { value: "0.97", label: "0.97" },
                  { value: "0.96", label: "0.96" },
                  { value: "0.95", label: "0.95" },
                  { value: "nocharm", label: t(($) => $.pricingv2["No charm"]) },
                  { value: "rounded", label: t(($) => $.pricingv2["Rounded"]) },
                ]}
              />
              <ConfPageLabel
                label={t(($) => $.pricingv2["Weekly Pricing Cycle"])}
                description={t(
                  ($) => $.pricingv2["If enabled, Dynamic Pricing will evaluate price changes on a weekly cycle."],
                )}
              />
              <Field
                name={"weekly_cycle"}
                component={[Select, { isDisabled: !userCanUpdateOrgSettings }]}
                dataSource={[
                  { value: false, label: t(($) => $.pricingv2["No"]) },
                  { value: true, label: t(($) => $.pricingv2["Yes"]) },
                ]}
              />

              <ConfPageLabel
                label={t(($) => $.pricingv2["Use Imported Inventory for Days on Hand"])}
                description={t(
                  ($) =>
                    $.pricingv2["Use the imported inventory to calculate Days on Hand threshold for pricing rules"],
                )}
              />
              <Field
                name={"use_incoming_inventory"}
                component={[Select, { isDisabled: !userCanUpdateOrgSettings }]}
                dataSource={[
                  { value: false, label: t(($) => $.pricingv2["No"]) },
                  { value: true, label: t(($) => $.pricingv2["Yes"]) },
                ]}
              />
              <ConfPageLabel
                label={t(($) => $.pricingv2["Inventory Source"])}
                description={t(
                  ($) =>
                    $.pricingv2[
                      "Which fulfillment channel's inventory to use for Days of Supply calculations. Default uses FBA with MFN fallback."
                    ],
                )}
              />
              <Field
                name={"inventory_bias"}
                component={[Select, { isDisabled: !userCanUpdateOrgSettings }]}
                dataSource={[
                  { value: "", label: t(($) => $.pricingv2["Default (FBA with MFN fallback)"]) },
                  { value: "FBA", label: t(($) => $.pricingv2["FBA Only"]) },
                  { value: "MFN", label: t(($) => $.pricingv2["MFN Only"]) },
                ]}
              />

              {form.values.inventory_bias !== "MFN" && (
                <>
                  <ConfPageLabel
                    label={t(($) => $.pricingv2["FBA Inventory Fields"])}
                    description={t(
                      ($) =>
                        $.pricingv2[
                          "Which FBA inventory columns to include when calculating inventory levels. By default, only Fulfillable Quantity is used."
                        ],
                    )}
                  />
                  <FbaInventoryFieldsControl
                    value={form.values.fba_inventory_fields}
                    onChange={(fields) => form.setValuesIn("fba_inventory_fields", fields)}
                    isDisabled={!userCanUpdateOrgSettings}
                  />
                </>
              )}

              <ConfPageLabel
                label={t(($) => $.pricingv2["Enable Advanced Days on Hand Rules"])}
                description={t(
                  ($) =>
                    $.pricingv2["Enable the more advanced Days on Hand rules to help you manage your inventory levels"],
                )}
              />
              <Field
                name={"has_inventory_rules"}
                component={[Select, { isDisabled: !userCanUpdateOrgSettings }]}
                dataSource={[
                  { value: false, label: t(($) => $.pricingv2["No"]) },
                  { value: true, label: t(($) => $.pricingv2["Yes"]) },
                ]}
                reactions={(field) => {
                  if (field.value) {
                    form.setValuesIn("inventory_rules", data.supply_target_program?.rules ?? defaultInventoryRules);
                  } else {
                    form.setValuesIn("inventory_rules", null);
                  }
                }}
              />
            </Grid>
            <Accordion title={t(($) => $.pricingv2["Prices to send"])} defaultExpanded>
              <Grid rowGap="medium" justifyItems={"start"} columns={2}>
                <Field
                  name={"send_standard_price"}
                  title={t(($) => $.pricingv2["Your Price"])}
                  component={[Checkbox, { isDisabled: !userCanUpdateOrgSettings }]}
                  reactions={(field) => {
                    field.query(".send_discounted_price").take((f) => {
                      if (!isField(f)) return;

                      if (!field.value && !f.value) {
                        field.selfErrors = [
                          t(($) => $.pricingv2['At least one of "Your Price" or "Sales Price" must be sent']),
                        ];
                      } else {
                        field.selfErrors = [];
                      }
                    });
                  }}
                />
                <Field
                  name={"send_discounted_price"}
                  title={t(($) => $.pricingv2["Sales Price"])}
                  component={[Checkbox, { isDisabled: !userCanUpdateOrgSettings }]}
                  reactions={(field) => {
                    field.query(".send_standard_price").take((f) => {
                      if (!isField(f)) return;

                      if (!field.value && !f.value) {
                        f.selfErrors = [
                          t(($) => $.pricingv2['At least one of "Your Price" or "Sales Price" must be sent']),
                        ];
                      } else {
                        f.selfErrors = [];
                      }
                    });
                  }}
                />
                <PricesErrorMessage />
                <Field
                  name={"send_b2b_price"}
                  title={t(($) => $.pricingv2["B2B Price"])}
                  component={[Checkbox, { isDisabled: !userCanUpdateOrgSettings }]}
                  reactions={(field) => {
                    field.query(".b2b_offset").take((f) => {
                      if (!isField(f)) return;
                      f.disabled = !field.value;
                      if (!field.value) {
                        f.value = { amount: 0, type: "percent" };
                      }
                    });
                  }}
                />
                <Field
                  name={"send_list_price"}
                  title={t(($) => $.pricingv2["List Price"])}
                  component={[Checkbox, { isDisabled: !userCanUpdateOrgSettings }]}
                  reactions={(field) => {
                    field.query(".list_price_offset").take((f) => {
                      if (!isField(f)) return;
                      f.disabled = !field.value;
                      if (!field.value) {
                        f.value = { amount: 0, type: "percent" };
                      }
                    });
                  }}
                />
                <Field
                  name={"b2b_offset"}
                  title={t(($) => $.pricingv2["B2B Offset"])}
                  component={[OffsetInput]}
                  validator={(value) =>
                    value.amount > 0 ? t(($) => $.pricingv2["B2B offset must be negative to give a discount"]) : true
                  }
                  reactions={(field) => {
                    field.query(".send_b2b_price").take((f) => {
                      if (!isField(f)) return;
                      if (f.value) {
                        field.description = t(
                          ($) => $.pricingv2["If B2C price is 110, B2B price is going to be {price}"],
                          {
                            price: applyOffset(110, field.value.amount, field.value.type),
                          },
                        );
                      } else {
                        field.description = t(($) => $.pricingv2["B2B price won't be sent"]);
                      }
                    });
                  }}
                />
                <Field
                  name={"list_price_offset"}
                  title={t(($) => $.pricingv2["List Price Offset"])}
                  component={[OffsetInput]}
                  validator={(value) =>
                    value.amount < 0 ?
                      t(($) => $.pricingv2["List price should exceed or be equal to offer price"])
                    : true
                  }
                  reactions={(field) => {
                    field.query(".send_list_price").take((f) => {
                      if (!isField(f)) return;
                      if (f.value) {
                        field.description = t(
                          ($) => $.pricingv2["If sell price is 110, list price is going to be {price}"],
                          {
                            price: applyOffset(110, field.value.amount, field.value.type),
                          },
                        );
                      } else {
                        field.description = t(($) => $.pricingv2["List price won't be sent"]);
                      }
                    });
                  }}
                />
              </Grid>
            </Accordion>
            <Accordion title={t(($) => $.pricingv2["Advanced Settings"])} defaultExpanded={false}>
              <Grid
                columnGap={"xlarge"}
                rowGap="medium"
                alignItems={"start"}
                gridTemplateColumns={"minmax(0,2fr) minmax(0,1fr)"}
                gridAutoRows={"1fr"}
              >
                <ConfPageLabel
                  label={t(($) => $.pricingv2["Look Back Days"])}
                  description={t(
                    ($) => $.pricingv2["How many historical days does the pricing algorithm use to make decisions."],
                  )}
                />
                <Field
                  name={"days_back"}
                  component={[
                    NumberInput,
                    {
                      minValue: 0,
                      formatOptions: {
                        maximumFractionDigits: 0,
                      },
                      isDisabled: !userCanUpdateOrgSettings,
                    },
                  ]}
                />

                <ConfPageLabel
                  label={t(($) => $.pricingv2["Sales Threshold"])}
                  description={t(
                    ($) =>
                      $.pricingv2[
                        "How many units sold does the price to have in order to be considered valid by Dynamic Pricing."
                      ],
                  )}
                />
                <Field
                  name={"sales_threshold"}
                  component={[
                    NumberInput,
                    {
                      minValue: 0,
                      formatOptions: {
                        maximumFractionDigits: 0,
                      },
                      isDisabled: !userCanUpdateOrgSettings,
                    },
                  ]}
                />

                <ConfPageLabel
                  label={t(($) => $.pricingv2["Days with Price"])}
                  description={t(
                    ($) =>
                      $.pricingv2[
                        "How many days does a price need to have been offered in order to be considered valid by Dynamic Pricing."
                      ],
                  )}
                />
                <Field
                  name={"days_with_price"}
                  component={[
                    NumberInput,
                    {
                      minValue: 0,
                      formatOptions: {
                        maximumFractionDigits: 0,
                      },
                      isDisabled: !userCanUpdateOrgSettings,
                    },
                  ]}
                />
              </Grid>
            </Accordion>

            {form.values.has_inventory_rules ?
              <Accordion title={t(($) => $.pricingv2["Days on Hand Rules"])} defaultExpanded={false}>
                <VStack>
                  <ExplainerBox>
                    {t(
                      ($) =>
                        $.pricingv2[
                          "The rule set below enables you to adjust your pricing strategies based on the remaining days of inventory on hand. The minimum and maximum limits compare the current Days on Hand to the Days on Hand target specified in each Dynamic Pricing plan's configuration. Their purpose is to ensure stock retention when inventory is low, enhance margins when inventory levels are stable, and reduce prices to increase sell-through rates during excess or overstock situations."
                        ],
                    )}
                  </ExplainerBox>
                  <Table bordered>
                    <thead>
                      <tr>
                        <th style={{ width: "250px" }}>{t(($) => $.pricingv2["Status"])}</th>
                        <th style={{ width: "180px" }}>{t(($) => $.pricingv2["Minimum (%)"])}</th>
                        <th style={{ width: "180px" }}>{t(($) => $.pricingv2["Maximum (%)"])}</th>
                        <th>{t(($) => $.pricingv2["Example"])}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <ArrayField name={"inventory_rules"}>
                        {(inventoryRulesField) => (
                          <>
                            <tr>
                              <td>
                                <strong>{t(($) => $.pricingv2["Deficient"])}</strong>
                                <div>
                                  <small>{t(($) => $.pricingv2["Running out of stock soon"])}</small>
                                </div>
                              </td>
                              <td>
                                <Field
                                  name={"0.min"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      css: css.raw({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  disabled
                                />
                              </td>
                              <td>
                                <Field
                                  name={"0.max"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      css: css.raw({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  reactions={(field) => {
                                    field.query("..[+].min").take((f) => {
                                      if (!isField(f)) return;
                                      f.value = field.value;
                                    });
                                  }}
                                />
                              </td>
                              <td>
                                {testDesiredInventoryLevel && testInventoryLevel ?
                                  <Trans
                                    t={t}
                                    i18nKey={($) =>
                                      $.pricingv2[
                                        "If the pricing plan has between <b>{min}</b> to <b>{max}</b> days of inventory on hand, it is considered deficient. Pricing plans within this range will adopt a more aggressive strategy to increase prices."
                                      ]
                                    }
                                    components={{
                                      b: <span className="fw-bold" style={{ color: trellisPalette[14] }} />,
                                    }}
                                    values={{
                                      min: inventoryRulesField.value[0].min,
                                      max: (inventoryRulesField.value[0].max / 100) * testDesiredInventoryLevel,
                                    }}
                                  />
                                : <Trans
                                    t={t}
                                    i18nKey={($) =>
                                      $.pricingv2[
                                        "If the pricing plan is within <b>{min}%</b> to <b>{max}%</b> of my desired inventory level, I am considered deficient. Pricing plans in this range will be more aggressive in increasing prices."
                                      ]
                                    }
                                    components={{
                                      b: <span className="fw-bold" style={{ color: trellisPalette[14] }} />,
                                    }}
                                    values={{
                                      min: inventoryRulesField.value[0].min,
                                      max: inventoryRulesField.value[0].max,
                                    }}
                                  />
                                }
                              </td>
                            </tr>
                            <tr>
                              <td>
                                <strong>{t(($) => $.pricingv2["Healthy"])}</strong>
                                <div>
                                  <small>{t(($) => $.pricingv2["Balanced inventory levels"])}</small>
                                </div>
                              </td>
                              <td>
                                <Field
                                  name={"1.min"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      css: css.raw({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  disabled
                                />
                              </td>
                              <td>
                                <Field
                                  name={"1.max"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      css: css.raw({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  reactions={(field) => {
                                    field.query("..[+].min").take((f) => {
                                      if (!isField(f)) return;
                                      f.value = field.value;
                                    });
                                  }}
                                />
                              </td>
                              <td>
                                {testDesiredInventoryLevel && testInventoryLevel ?
                                  <Trans
                                    t={t}
                                    i18nKey={($) =>
                                      $.pricingv2[
                                        "If the pricing plan has between <b>{min}</b> to <b>{max}</b> days of inventory on hand, it is considered healthy. Pricing plans in this range will be follow your chosen strategy (e.g. volume, balanced or margin)"
                                      ]
                                    }
                                    components={{
                                      b: <span className="fw-bold" style={{ color: trellisPalette[14] }} />,
                                    }}
                                    values={{
                                      min: (inventoryRulesField.value[1].min / 100) * testDesiredInventoryLevel,
                                      max: (inventoryRulesField.value[1].max / 100) * testDesiredInventoryLevel,
                                    }}
                                  />
                                : <Trans
                                    t={t}
                                    i18nKey={($) =>
                                      $.pricingv2[
                                        "If the pricing plan is within <b>{min}%</b> to <b>{max}%</b> of my desired inventory level, it am considered healthy. Pricing plans in this range will be follow your chosen strategy (e.g. volume, balanced or margin)"
                                      ]
                                    }
                                    components={{
                                      b: <span className="fw-bold" style={{ color: trellisPalette[14] }} />,
                                    }}
                                    values={{
                                      min: inventoryRulesField.value[1].min,
                                      max: inventoryRulesField.value[1].max,
                                    }}
                                  />
                                }
                              </td>
                            </tr>
                            <tr>
                              <td>
                                <strong>{t(($) => $.pricingv2["Excess"])}</strong>
                                <div>
                                  <small>{t(($) => $.pricingv2["Sell through rate too low"])}</small>
                                </div>
                              </td>
                              <td>
                                <Field
                                  name={"2.min"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      css: css.raw({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  disabled
                                />
                              </td>
                              <td>
                                <Field
                                  name={"2.max"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      css: css.raw({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  reactions={(field) => {
                                    field.query("..[+].min").take((f) => {
                                      if (!isField(f)) return;
                                      f.value = field.value;
                                    });
                                  }}
                                />
                              </td>
                              <td>
                                {testDesiredInventoryLevel && testInventoryLevel ?
                                  <Trans
                                    t={t}
                                    i18nKey={($) =>
                                      $.pricingv2[
                                        "If the pricing plan has between <b>{min}</b> to <b>{max}</b> on hand, it is considered to have excess inventory. Pricing plans in this range will lean towards price cuts to increase the sell through rate."
                                      ]
                                    }
                                    components={{
                                      b: <span className="fw-bold" style={{ color: trellisPalette[14] }} />,
                                    }}
                                    values={{
                                      min: (inventoryRulesField.value[2].min / 100) * testDesiredInventoryLevel,
                                      max: (inventoryRulesField.value[2].max / 100) * testDesiredInventoryLevel,
                                    }}
                                  />
                                : <Trans
                                    t={t}
                                    i18nKey={($) =>
                                      $.pricingv2[
                                        "If the pricing plan is within <b>{min}%</b> to <b>{max}%</b> of my desired days on hand, there is excess inventory. Pricing plans in this range will lean towards price cuts to increase the sell through rate."
                                      ]
                                    }
                                    components={{
                                      b: <span className="fw-bold" style={{ color: trellisPalette[14] }} />,
                                    }}
                                    values={{
                                      min: inventoryRulesField.value[2].min,
                                      max: inventoryRulesField.value[2].max,
                                    }}
                                  />
                                }
                              </td>
                            </tr>
                            <tr>
                              <td>
                                <strong>{t(($) => $.pricingv2["Overstock"])}</strong>
                                <div>
                                  <small>{t(($) => $.pricingv2["Clearance needed to increase sales"])}</small>
                                </div>
                              </td>
                              <td>
                                <Field
                                  name={"3.min"}
                                  component={[
                                    NumberInput,
                                    {
                                      endIcon: "%",
                                      css: css.raw({
                                        maxWidth: "150px",
                                      }),
                                    },
                                  ]}
                                  disabled
                                />
                              </td>
                              <td>
                                <TextField
                                  value={"Infinity"}
                                  isDisabled
                                  css={{
                                    maxWidth: "150px",
                                  }}
                                />
                              </td>
                              <td>
                                {testDesiredInventoryLevel && testInventoryLevel ?
                                  <Trans
                                    t={t}
                                    i18nKey={($) =>
                                      $.pricingv2[
                                        "If the pricing plan is over <b>{min}</b> it is considered to overstock. Pricing plans in this range will be very aggressive in lowering prices to improve the sell through rate."
                                      ]
                                    }
                                    components={{
                                      b: <span className="fw-bold" style={{ color: trellisPalette[14] }} />,
                                    }}
                                    values={{
                                      min: (inventoryRulesField.value[3].min / 100) * testDesiredInventoryLevel,
                                    }}
                                  />
                                : <Trans
                                    t={t}
                                    i18nKey={($) =>
                                      $.pricingv2[
                                        "If the pricing plan is over <b>{min}%</b> of the desired days on hand level, it is overstock. Pricing plans in this range will be very aggressive in lowering prices to improve the sell through rate."
                                      ]
                                    }
                                    components={{
                                      b: <span className="fw-bold" style={{ color: trellisPalette[14] }} />,
                                    }}
                                    values={{
                                      min: inventoryRulesField.value[3].min,
                                    }}
                                  />
                                }
                              </td>
                            </tr>
                          </>
                        )}
                      </ArrayField>
                    </tbody>
                  </Table>

                  <h5 className={css({ margin: 0, fontWeight: 700 })}>{t(($) => $.pricingv2["Test Your Rules"])}</h5>

                  <ExplainerBox>
                    <div>
                      <Trans
                        t={t}
                        i18nKey={($) =>
                          $.pricingv2[
                            "Test your rules by inputting an example of <em>desired days on hand</em> and the <em>current days on hand</em> for a pricing plan. The system will then determine the appropriate status for your pricing plan."
                          ]
                        }
                        components={{ em: <em /> }}
                      />
                    </div>
                  </ExplainerBox>
                  <HStack>
                    <NumberInputBase
                      label={t(($) => $.pricingv2["Desired Days on Hand"])}
                      value={testDesiredInventoryLevel}
                      onChange={setTestDesiredInventoryLevel}
                    />
                    <NumberInputBase
                      label={t(($) => $.pricingv2["Example Days on Hand"])}
                      value={testInventoryLevel}
                      onChange={setTestInventoryLevel}
                    />
                    <TextField
                      label={t(($) => $.pricingv2["Status"])}
                      value={(() => {
                        const status = getInventoryRuleStatus(
                          testInventoryLevel,
                          testDesiredInventoryLevel,
                          form.values.inventory_rules ?? [],
                        );
                        switch (status) {
                          case "deficient":
                            return t(($) => $.pricingv2["Deficient"]);
                          case "healthy":
                            return t(($) => $.pricingv2["Healthy"]);
                          case "excess":
                            return t(($) => $.pricingv2["Excess"]);
                          case "overstock":
                            return t(($) => $.pricingv2["Overstock"]);
                          default:
                            return toTitleCase(status);
                        }
                      })()}
                      isDisabled
                    />
                    <TextField
                      label={t(($) => $.pricingv2["Pricing Strategy"])}
                      value={(() => {
                        const status =
                          getInventoryRuleStatus(
                            testInventoryLevel,
                            testDesiredInventoryLevel,
                            form.values.inventory_rules ?? [],
                          ) ?? "default";
                        switch (RULE_TO_STRATEGY_KEY[status]) {
                          case "Increase Prices":
                            return t(($) => $.pricingv2["Increase Prices"]);
                          case "Balanced Strategy":
                            return t(($) => $.pricingv2["Balanced Strategy"]);
                          case "Decrease Prices":
                            return t(($) => $.pricingv2["Decrease Prices"]);
                          case "Aggressive Price Reduction":
                            return t(($) => $.pricingv2["Aggressive Price Reduction"]);
                          default:
                            return t(($) => $.pricingv2["Unknown"]);
                        }
                      })()}
                      isDisabled
                    />
                  </HStack>
                </VStack>
              </Accordion>
            : null}
            <Center marginTop={"small"}>
              <Button
                isDisabled={!userCanUpdateOrgSettings}
                isPending={form.submitting}
                onPress={async () => {
                  await form.submit(onSubmit);
                  form.setInitialValues(form.values);
                  await form.reset();
                }}
              >
                {t(($) => $.pricingv2["Save"])}
              </Button>
            </Center>
          </Card>
        )}
      </FormConsumer>
    </FormProvider>
  );
}

export const Route = createFileRoute("/_authenticated/user/pricing/configuration")({
  component: Settings,
});
